import type { ReactElement } from "react";
import { ConfirmDialog } from "../molecules/index.js";

export interface CanvasWidgetRemoveDialogProps {
  readonly open: boolean;
  /** The widget's registered display label (`def.label`), never a host title. */
  readonly label: string;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}

/**
 * The confirmation in front of a canvas widget's header trash button.
 *
 * Internal to `CanvasWidget` and deliberately not on the barrel: the shared
 * header is the ONE user-facing removal path for SOLVE, VSM and Hoshin canvas
 * widgets, so the confirmation lives with it and no feature has to carry a
 * copy. Everything else about removal is unchanged — the host's `onRemove`
 * still owns persistence, Yjs, cache, audit and error handling, and it is
 * invoked exactly once, only from `Remove widget`.
 *
 * Built on the shared `ConfirmDialog` so focus trapping, return-focus, Escape
 * and outside-press dismissal are the engine's, not this file's. Initial focus
 * lands on Cancel: the destructive action must be a deliberate second press,
 * not the default of an accidental Enter.
 */
export function CanvasWidgetRemoveDialog({
  open,
  label,
  onClose,
  onConfirm,
}: CanvasWidgetRemoveDialogProps): ReactElement {
  return (
    <ConfirmDialog
      open={open}
      title={`Remove ${label}?`}
      confirmLabel="Remove widget"
      onClose={onClose}
      onConfirm={onConfirm}
    >
      This widget will be removed from the canvas.
    </ConfirmDialog>
  );
}
